import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Label } from '@apps/web/components/ui/label';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';

type RemovalReason =
    | 'cancelled'
    | 'filled'
    | 'filled-externally'
    | 'no_longer_available';

type CancelShiftDialogProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly shiftIds: ReadonlyArray<string>;
    readonly targetLabel: string;
    readonly organizationId: string;
    readonly outreachId: string;
    readonly onSuccess: () => void;
    readonly onPartialSuccess: (failedShiftIds: ReadonlyArray<string>) => void;
};

export function CancelShiftDialog({
    open,
    onOpenChange,
    shiftIds,
    targetLabel,
    organizationId,
    outreachId,
    onSuccess,
    onPartialSuccess,
}: CancelShiftDialogProps) {
    const [isCancelling, setIsCancelling] = useState(false);
    const [sendNotifications, setSendNotifications] = useState(false);
    const [removalReason, setRemovalReason] =
        useState<RemovalReason>('cancelled');
    const [includeNonResponders, setIncludeNonResponders] = useState(false);
    const selectedShiftIds = [...shiftIds];
    const selectedShiftKey = selectedShiftIds.join('|');
    const isMultiShiftArchive = selectedShiftIds.length > 1;

    useEffect(() => {
        if (open) {
            setSendNotifications(false);
            setRemovalReason('cancelled');
            setIncludeNonResponders(false);
        }
    }, [open, selectedShiftKey, targetLabel]);

    const handleConfirm = async () => {
        if (selectedShiftIds.length === 0) {
            return;
        }

        setIsCancelling(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/callouts/{callout_id}/shifts/cancel',
                pathParams: {
                    organization_id: organizationId,
                    callout_id: outreachId,
                },
                body: {
                    shift_ids: selectedShiftIds,
                    send_notifications: sendNotifications,
                    removal_reason: removalReason,
                    notification_reason: sendNotifications
                        ? removalReason
                        : null,
                    include_non_responders:
                        sendNotifications && includeNonResponders,
                },
            });
            const failures = response.failures ?? [];
            if (failures.length > 0) {
                const failedShiftIds = failures.map(
                    (failure) => failure.shift_id,
                );
                const archivedCount =
                    response.total_cancelled ??
                    selectedShiftIds.length - failures.length;

                if (archivedCount > 0) {
                    toast.warning(
                        `${archivedCount} ${
                            archivedCount === 1 ? 'shift was' : 'shifts were'
                        } archived, but ${failures.length} ${
                            failures.length === 1 ? 'shift' : 'shifts'
                        } failed.`,
                        {
                            description: failures
                                .map((failure) => failure.error)
                                .join(' '),
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                    onPartialSuccess(failedShiftIds);
                    onOpenChange(false);
                } else {
                    toast.error(
                        isMultiShiftArchive
                            ? 'Failed to archive shifts'
                            : 'Failed to archive shift',
                        {
                            description: failures
                                .map((failure) => failure.error)
                                .join(' '),
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                }
                return;
            }
            if (sendNotifications) {
                toast.success(
                    isMultiShiftArchive
                        ? 'Shifts archived. Notifying caregivers.'
                        : 'Shift archived. Notifying caregivers.',
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
            } else {
                toast.success(
                    isMultiShiftArchive ? 'Shifts archived' : 'Shift archived',
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
            }
            onSuccess();
            onOpenChange(false);
        } catch (error) {
            const apiError = error as { body?: { detail?: string } };
            toast.error(
                apiError?.body?.detail ??
                    (isMultiShiftArchive
                        ? 'Failed to archive shifts'
                        : 'Failed to archive shift'),
                {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                },
            );
        } finally {
            setIsCancelling(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isMultiShiftArchive
                            ? 'Archive shifts?'
                            : 'Archive shift?'}
                    </DialogTitle>
                    <DialogDescription>
                        {isMultiShiftArchive ? (
                            <span>
                                This will remove{' '}
                                <strong>
                                    {selectedShiftIds.length} shifts
                                </strong>{' '}
                                from this outreach. Caregivers will no longer be
                                contacted about these shifts.
                            </span>
                        ) : (
                            <span>
                                This will remove the shift for{' '}
                                <strong>{targetLabel}</strong> from this
                                outreach. Caregivers will no longer be contacted
                                about this shift.
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="flex items-start gap-3">
                        <Checkbox
                            id="send-notifications"
                            checked={sendNotifications}
                            onCheckedChange={(checked) =>
                                setSendNotifications(checked === true)
                            }
                        />
                        <Label
                            htmlFor="send-notifications"
                            className="cursor-pointer text-sm font-medium"
                        >
                            Notify caregivers that{' '}
                            {isMultiShiftArchive
                                ? 'these shifts are'
                                : 'this shift is'}{' '}
                            no longer available
                        </Label>
                    </div>
                    <div className="space-y-3">
                        <Label className="text-muted-foreground text-xs font-medium">
                            Archive reason:
                        </Label>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    id="reason-cancelled"
                                    name="removal-reason"
                                    value="cancelled"
                                    checked={removalReason === 'cancelled'}
                                    onChange={() =>
                                        setRemovalReason('cancelled')
                                    }
                                    className="h-4 w-4"
                                />
                                <Label
                                    htmlFor="reason-cancelled"
                                    className="cursor-pointer text-sm font-normal"
                                >
                                    Archived -{' '}
                                    <span className="text-muted-foreground">
                                        “shift has been cancelled”
                                    </span>
                                </Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    id="reason-filled-externally"
                                    name="removal-reason"
                                    value="filled-externally"
                                    checked={
                                        removalReason === 'filled-externally'
                                    }
                                    onChange={() =>
                                        setRemovalReason('filled-externally')
                                    }
                                    className="h-4 w-4"
                                />
                                <Label
                                    htmlFor="reason-filled-externally"
                                    className="cursor-pointer text-sm font-normal"
                                >
                                    Filled externally -{' '}
                                    <span className="text-muted-foreground">
                                        “shift has been filled”
                                    </span>
                                </Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    id="reason-unavailable"
                                    name="removal-reason"
                                    value="no_longer_available"
                                    checked={
                                        removalReason === 'no_longer_available'
                                    }
                                    onChange={() =>
                                        setRemovalReason('no_longer_available')
                                    }
                                    className="h-4 w-4"
                                />
                                <Label
                                    htmlFor="reason-unavailable"
                                    className="cursor-pointer text-sm font-normal"
                                >
                                    No longer available -{' '}
                                    <span className="text-muted-foreground">
                                        “shift is no longer available”
                                    </span>
                                </Label>
                            </div>
                        </div>
                        {sendNotifications && (
                            <div className="mt-4 flex items-start gap-3">
                                <Checkbox
                                    id="include-non-responders"
                                    checked={includeNonResponders}
                                    onCheckedChange={(checked) =>
                                        setIncludeNonResponders(
                                            checked === true,
                                        )
                                    }
                                />
                                <Label
                                    htmlFor="include-non-responders"
                                    className="cursor-pointer text-sm font-normal"
                                >
                                    Include caregivers who didn’t respond
                                </Label>
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isCancelling}
                    >
                        Keep shift
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={isCancelling}
                    >
                        {isCancelling ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        {isMultiShiftArchive
                            ? 'Archive shifts'
                            : 'Archive shift'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
